import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Button,
  Badge,
  Dropdown,
  Alert,
  Spinner,
} from 'react-bootstrap';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { activityService, timesheetService, reportService } from '../services';
import { formatFileSize } from '../helpers';
import {
  addReportJob,
  updateReportJob,
  removeReportJob,
} from '../actions/reportJobs';

const resolveFileName = (name, type) => {
  if (!name) return 'report.xlsx';
  if (type === 'summarized_timesheet')
    return name.replace(/^timesheet/, 'summarized-timesheet');
  if (type === 'timesheet')
    return name.replace(/^timesheet/, 'detailed-timesheet');
  return name;
};

const ReportGenerationModal = ({
  reportType,
  showModal = false,
  setShowModal,
  formParams,
  dateRange,
  permissions,
  handleLoader,
  isLoading,
  exportReportTimeZone,
}) => {
  const dispatch = useDispatch();
  const backgroundJobs = useSelector((state) => state.reportJobsReducer.jobs);

  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState('processing'); // 'processing', 'completed', 'failed'
  const [progress, setProgress] = useState(0);
  const [downloadLink, setDownloadLink] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState('');

  const pollingIntervalRef = useRef(null);
  // Map of { [jobId]: intervalId } for background jobs resumed from Redux on mount
  const backgroundPollingRef = useRef({});

  // API map based on reportType
  const apiMap = {
    summary: {
      start: activityService.get_async_schedules_summary,
    },
    activity: {
      start: activityService.get_async_schedules_activity,
    },
    timesheet: {
      start: timesheetService.get_async_timesheet_report,
    },
    summarized_timesheet: {
      start: timesheetService.get_async_timesheet_report,
    },
    adherence: {
      start: reportService.get_async_adherence_report,
    },
    attendance: {
      start: reportService.get_async_attendance_report,
    },
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Poll job status
  const checkJobStatus = async (jobId) => {
    activityService
      .get_async_schedules_summary_status(jobId)
      .then((response) => {
        if (response.data) {
          const { data } = response;
          setProgress(data.progress || 0);
          if (data.status === 'completed') {
            setJobStatus('completed');
            setDownloadLink(data.downloadUrl);
            setFileName(resolveFileName(data.fileName, reportType));
            setFileSize(data.fileSize || 0);
            stopPolling();

            // Update background job if exists
            dispatch(
              updateReportJob(jobId, {
                status: 'completed',
                progress: 100,
                downloadUrl: data.downloadUrl,
                fileName: resolveFileName(data.fileName, reportType),
                size: data.fileSize ? formatFileSize(data.fileSize) : '0',
              })
            );
          } else if (data.status === 'failed') {
            setJobStatus('failed');
            setErrorMessage(data.error || 'Report generation failed.');
            stopPolling();

            // Update background job if exists
            dispatch(updateReportJob(jobId, { status: 'failed' }));
          } else {
            // Still processing
            setJobStatus('processing');
          }
        }
      })
      .catch((error) => {
        // Do nothing
      });
  };

  const startPolling = (jobId) => {
    stopPolling(); // Clear any existing interval
    pollingIntervalRef.current = setInterval(() => {
      checkJobStatus(jobId);
    }, 5000); // Poll every 5 seconds
  };

  // API call to generate report
  const generateReport = async () => {
    handleLoader(true);

    setShowModal(true);
    setJobStatus('processing');
    setProgress(0);
    setDownloadLink(null);
    setErrorMessage('');

    let params;

    if (
      ['timesheet', 'summarized_timesheet', 'adherence', 'attendance'].includes(
        reportType
      )
    ) {
      // formParams is already the cleaned API payload built in handleGenerateData
      params = { ...formParams };
    } else {
      const {
        project,
        site,
        clientId,
        teamMember,
        billable,
        status,
        timeZone,
        shiftId,
        team,
      } = formParams;

      const dataFromDate = moment(dateRange.startDate).format('YYYY-MM-DD');
      const dataToDate = moment(dateRange.endDate).format('YYYY-MM-DD');
      params = {
        teamOwnerId: localStorage.getItem('teamOwnerId'),
        teamMemberId: teamMember?.length > 0 ? teamMember : [],
        siteId: site?.length > 0 ? site : [],
        clientId: clientId?.length > 0 ? clientId : [],
        projectId: project?.length > 0 ? project : [],
        fromDate: dataFromDate.concat(' 00:00:00'),
        toDate: dataToDate.concat(' 23:59:59'),
        page: 1,
        status: status || 'all',
        billable: billable || 'all',
        timeZone: exportReportTimeZone === 'UTC' ? 'UTC' : timeZone,
        shiftId,
        teamGroupId: [team].flat(),
      };

      if (
        teamMember === 'all' ||
        teamMember.length === 0 ||
        teamMember.includes('all') ||
        !permissions?.includes('team_member_view')
      ) {
        delete params.teamMemberId;
      }

      if (status === 'all') {
        delete params.status;
      }

      if (site.length === 0 || site.includes('all')) {
        delete params.siteId;
      }

      if (clientId.length === 0 || clientId.includes('all')) {
        delete params.clientId;
      }

      if (shiftId.length === 0 || shiftId.includes('all')) {
        delete params.shiftId;
      }
      if (
        (params.projectId && params.projectId.length === 0) ||
        (params.projectId && params.projectId.includes('all'))
      ) {
        delete params.projectId;
      }

      if (billable === 'all') {
        delete params.billable;
      }

      if (formParams.team === 'all' || !formParams.team) {
        delete params.teamGroupId;
      }
    }

    apiMap[reportType]
      .start(params)
      .then((response) => {
        if (response.data) {
          const { jobId } = response.data;
          setProgress(10);
          setCurrentJobId(jobId);
          startPolling(jobId);
        }
      })
      .catch((error) => {
        setJobStatus('failed');
        setErrorMessage(
          'Failed to initiate report generation. Please try again.'
        );
      })
      .finally(() => {
        handleLoader(false);
      });
  };

  // Poll status for a background job — only updates Redux, not modal state
  const checkBackgroundJobStatus = (jobId) => {
    activityService
      .get_async_schedules_summary_status(jobId)
      .then((response) => {
        if (response.data) {
          const { data } = response;
          if (data.status === 'completed') {
            const jobReportType = backgroundJobs.find(
              (j) => j.id === jobId
            )?.reportType;
            dispatch(
              updateReportJob(jobId, {
                status: 'completed',
                progress: 100,
                downloadUrl: data.downloadUrl,
                fileName: resolveFileName(data.fileName, jobReportType),
                size: data.fileSize ? formatFileSize(data.fileSize) : '0',
              })
            );
            if (backgroundPollingRef.current[jobId]) {
              clearInterval(backgroundPollingRef.current[jobId]);
              delete backgroundPollingRef.current[jobId];
            }
          } else if (data.status === 'failed') {
            dispatch(updateReportJob(jobId, { status: 'failed' }));
            if (backgroundPollingRef.current[jobId]) {
              clearInterval(backgroundPollingRef.current[jobId]);
              delete backgroundPollingRef.current[jobId];
            }
          }
        }
      })
      .catch(() => {});
  };

  // On mount: resume polling for any processing jobs persisted in Redux
  useEffect(() => {
    backgroundJobs
      .filter((j) => j.status === 'processing')
      .forEach((job) => {
        if (!backgroundPollingRef.current[job.id]) {
          backgroundPollingRef.current[job.id] = setInterval(() => {
            checkBackgroundJobStatus(job.id);
          }, 5000);
        }
      });
    return () => {
      Object.values(backgroundPollingRef.current).forEach(clearInterval);
      backgroundPollingRef.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clean up modal polling on unmount
  useEffect(() => () => stopPolling(), []);
  useEffect(() => {
    if (showModal) {
      generateReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const handleRunInBackground = () => {
    const { startDate, endDate } = dateRange;

    const fromDate = moment(startDate).format('MMM DD, YYYY');
    const toDate = moment(endDate).format('MMM DD, YYYY');

    const job = {
      id: currentJobId,
      name:
        {
          timesheet: 'Detailed Timesheet Report',
          summarized_timesheet: 'Summarized Timesheet Report',
          activity: 'Schedule Activity Report',
          summary: 'Schedule Summary Report',
          adherence: 'Adherence Report',
          attendance: 'Attendance Report',
        }[reportType] || 'Schedule Summary Report',
      dateRange: `${fromDate} - ${toDate}`,
      status: jobStatus,
      progress,
      downloadUrl: downloadLink,
      fileName,
      size: `${fileSize > 0 ? formatFileSize(fileSize) : 0}`,
      reportType,
    };

    // Guard against duplicate (e.g. user clicks button twice)
    if (!backgroundJobs.some((j) => j.id === currentJobId)) {
      dispatch(addReportJob(job));
    }
    setShowModal(false);
    // pollingIntervalRef keeps running in the background
  };

  const handleDownload = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    stopPolling();
  };

  const removeBackgroundJob = (jobId) => {
    dispatch(removeReportJob(jobId));
    if (backgroundPollingRef.current[jobId]) {
      clearInterval(backgroundPollingRef.current[jobId]);
      delete backgroundPollingRef.current[jobId];
    }
  };

  const getCompletedJobsCount = () =>
    backgroundJobs.filter((job) => job.status === 'completed').length;

  const ProcessingView = () => (
    <>
      <Modal.Body>
        <div className="d-flex align-items-center mb-4">
          <div
            className="bg-opacity-10 bg-primary rounded-circle d-flex align-items-center justify-content-center me-3 border border-1"
            style={{ width: '48px', height: '48px' }}
          >
            <i
              className="mdi mdi-clock-outline text-primary"
              style={{ fontSize: '24px' }}
            />
          </div>
          <div>
            <h4 className="mb-1">Generating Report...</h4>
            <small className="text-muted">
              {
                {
                  summary: 'Schedule Summary Report',
                  activity: 'Schedule Activity Report',
                  timesheet: 'Detailed Timesheet Report',
                  summarized_timesheet: 'Summarized Timesheet Report',
                  adherence: 'Adherence Report',
                  attendance: 'Attendance Report',
                }[reportType]
              }
            </small>
          </div>
        </div>

        {!isLoading && (
          <div className="mb-4 d-flex align-items-center justify-content-center">
            <Spinner
              animation="border"
              className="text-primary"
              role="status"
              size="md"
            />
          </div>
        )}

        <Alert variant="light" className="mb-0">
          Your report is being generated. This may take a few minutes depending
          on the data size. You can wait here or run the process in the
          background.
        </Alert>
      </Modal.Body>

      <Modal.Footer className="justify-content-between">
        <Button variant="outline-primary" onClick={handleCloseModal}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleRunInBackground}>
          Run in Background
        </Button>
      </Modal.Footer>
    </>
  );

  const CompletedView = () => (
    <>
      <Modal.Body>
        <div className="d-flex align-items-center mb-4">
          <div
            className="bg-opacity-10 bg-success rounded-circle d-flex align-items-center justify-content-center me-3"
            style={{ width: '48px', height: '48px' }}
          >
            <i
              className="mdi mdi-check-circle text-success"
              style={{ fontSize: '24px' }}
            />
          </div>
          <div>
            <h4 className="mb-1">Report Ready!</h4>
            <small className="text-muted">
              Your report has been generated successfully
            </small>
          </div>
        </div>

        <Alert variant="light" className="mb-0">
          <p className="mb-2 fw-medium">Your report is ready to download:</p>
          <p className="mb-1 font-monospace small text-break">{fileName}</p>
          <small className="text-muted">
            File size: {fileSize > 0 ? formatFileSize(fileSize) : 0}
          </small>
        </Alert>
      </Modal.Body>

      <Modal.Footer className="justify-content-between">
        <Button variant="outline-primary" onClick={handleCloseModal}>
          Close
        </Button>
        <Button variant="primary" onClick={() => handleDownload(downloadLink)}>
          <i className="mdi mdi-download me-2" />
          Download
        </Button>
      </Modal.Footer>
    </>
  );

  const FailedView = () => (
    <>
      <Modal.Body>
        <div className="d-flex align-items-center mb-4">
          <div
            className="bg-opacity-10 bg-danger rounded-circle d-flex align-items-center justify-content-center me-3"
            style={{ width: '48px', height: '48px' }}
          >
            <i
              className="mdi mdi-alert-circle text-danger"
              style={{ fontSize: '24px' }}
            />
          </div>
          <div>
            <h4 className="mb-1">Generation Failed</h4>
            <small className="text-muted">
              An error occurred during report generation
            </small>
          </div>
        </div>

        <Alert variant="light" className="mb-0">
          {errorMessage && (
            <p className="mb-2 text-danger small">{errorMessage}</p>
          )}
          <strong>Suggestions:</strong>
          <br />
          • Try reducing the date range
          <br />
          • Select fewer team members or projects
          <br />
          • Ensure you have the required access permissions to view the data
          <br />• Contact support if the issue persists
        </Alert>
      </Modal.Body>

      <Modal.Footer className="justify-content-between">
        <Button variant="outline-primary" onClick={handleCloseModal}>
          Close
        </Button>
        <Button variant="primary" onClick={generateReport}>
          Try Again
        </Button>
      </Modal.Footer>
    </>
  );

  const NotificationBadge = () => {
    const completedCount = getCompletedJobsCount();
    const activeCount = backgroundJobs.length;

    if (activeCount === 0) return null;

    return (
      <div
        className="position-fixed"
        style={{ top: '85px', right: '20px', zIndex: 1050 }}
      >
        <Dropdown show={showNotifications} onToggle={setShowNotifications}>
          <Dropdown.Toggle
            variant="light"
            className="border shadow-sm d-flex align-items-center"
            style={{ borderRadius: '8px', gap: '8px' }}
          >
            <i
              className={`mdi mdi-bell ${
                completedCount > 0 ? 'text-primary' : 'text-primary'
              }`}
              style={{ fontSize: '18px' }}
            />
            <span className="small fw-medium">
              {completedCount > 0
                ? `${completedCount} Report${
                    completedCount > 1 ? 's' : ''
                  } Ready`
                : `${activeCount} Generating`}
            </span>
            {completedCount > 0 && (
              <Badge
                bg="success"
                pill
                className="position-absolute top-0 start-100 translate-middle"
              >
                {completedCount}
              </Badge>
            )}
          </Dropdown.Toggle>

          <Dropdown.Menu
            align="end"
            className="shadow-lg"
            style={{ width: '380px', maxHeight: '500px', overflowY: 'auto' }}
          >
            <div className="bg-light px-3 py-2 border-bottom">
              <h5 className="mb-0">Report Generation</h5>
            </div>

            <div>
              {backgroundJobs.map((job) => (
                <div key={job.id} className="px-3 py-3 border-bottom">
                  <div className="d-flex gap-3">
                    <div
                      className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0
                      ${
                        // eslint-disable-next-line no-nested-ternary
                        job.status === 'completed'
                          ? 'bg-opacity-10 bg-success'
                          : job.status === 'failed'
                          ? 'bg-opacity-10 bg-danger '
                          : 'bg-opacity-10 bg-primary '
                      }`}
                      style={{ width: '40px', height: '40px' }}
                    >
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {job.status === 'completed' ? (
                        <i
                          className="mdi mdi-check-circle text-success"
                          style={{ fontSize: '20px' }}
                        />
                      ) : job.status === 'failed' ? (
                        <i
                          className="mdi mdi-alert-circle text-danger"
                          style={{ fontSize: '20px' }}
                        />
                      ) : (
                        <i
                          className="mdi mdi-file-document text-primary"
                          style={{ fontSize: '20px' }}
                        />
                      )}
                    </div>

                    <div className="flex-grow-1 min-w-0">
                      <p className="mb-1 fw-medium small">
                        {job.name}
                        {job.status === 'completed' && (
                          <>
                            <br />
                            <small className="text-muted">
                              Ready to download
                            </small>
                          </>
                        )}
                      </p>

                      {job.status === 'processing' && (
                        <div>
                          <div
                            className="d-flex justify-content-between mb-1"
                            style={{ fontSize: '0.75rem' }}
                          >
                            <span className="text-muted">Processing...</span>
                          </div>
                          <Spinner
                            animation="border"
                            className="text-primary"
                            role="status"
                            size="sm"
                          />
                        </div>
                      )}
                    </div>

                    <div className="d-flex flex-row align-items-start gap-2">
                      {job.status === 'completed' && (
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => handleDownload(job.downloadUrl)}
                        >
                          <i
                            className="mdi mdi-download"
                            style={{ fontSize: '14px' }}
                          />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => removeBackgroundJob(job.id)}
                      >
                        <i
                          className="mdi mdi-close"
                          style={{ fontSize: '14px' }}
                        />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  };

  return (
    <>
      <div>
        {/* Notification Badge */}
        <NotificationBadge />

        {/* Main Modal */}
        <Modal
          show={showModal}
          onHide={handleCloseModal}
          centered
          backdrop="static"
        >
          <Modal.Header closeButton>
            <Modal.Title className="mb-0">Report Generation</Modal.Title>
          </Modal.Header>

          {jobStatus === 'processing' && <ProcessingView />}
          {jobStatus === 'completed' && <CompletedView />}
          {jobStatus === 'failed' && <FailedView />}
        </Modal>
      </div>
    </>
  );
};

ReportGenerationModal.propTypes = {
  reportType: PropTypes.string,
  showModal: PropTypes.bool,
  setShowModal: PropTypes.func,
  formParams: PropTypes.object,
  dateRange: PropTypes.object,
  permissions: PropTypes.array,
  handleLoader: PropTypes.func,
  isLoading: PropTypes.bool,
  exportReportTimeZone: PropTypes.string,
};

export default ReportGenerationModal;
